// -----------------------------------------------------------------------------
// Date/Time Utils
// -----------------------------------------------------------------------------

import { DatePickerMode } from '@/legacy/types';

import { DAYS_IN_MILLISECONDS, ISO_RE } from '@/legacy/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getLanguage, capitalizeFirstLetter } from './language';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getBrowserTimezone = (): string => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/*============================================================================
  ISO CONVERTERS 
============================================================================*/

/**
 * Convert Date to ISO 8601 string at midnight in user's local timezone
 * Example: Date(2025, 10, 18) -> "2025-11-18T00:00:00+01:00"
 */
export const toISODateLocal = (d: Date): string => {
	const userTimezone = dayjs.tz.guess();

	return dayjs(d)
		.tz(userTimezone)
		.startOf('day') // Sets to 00:00:00
		.format();
};

export const toISODateTimeLocal = (d: Date): string => {
	const userTimezone = dayjs.tz.guess();
	return dayjs(d).tz(userTimezone).format();
};

/**
 * Parse ISO 8601 datetime string to Date object
 * Example: "2025-11-18T00:00:00+01:00" -> Date object
 */
export const fromISODateTimeLocal = (iso: string): Date => {
	const result = dayjs(iso).toDate();
	return result;
};

/**
 * Convert local time string to ISO 8601 in user's timezone
 * Input: "14:30", Date(2025, 10, 18)
 * Output: "2025-11-18T14:30:00+01:00"
 */
export const convertLocalTimeToISO = (timeStr: string, onDate: Date): string | undefined => {
	if (!timeStr) return undefined;

	const [hours, minutes] = timeStr.split(':').map(Number);
	if (Number.isNaN(hours) || Number.isNaN(minutes)) return undefined;

	const userTimezone = dayjs.tz.guess();

	const localDate = new Date(
		onDate.getFullYear(),
		onDate.getMonth(),
		onDate.getDate(),
		hours,
		minutes,
		0,
		0,
	);
	const localDateTime = dayjs.tz(localDate, userTimezone);

	return localDateTime.format();
};

/**
 * Adjust date to end of day (23:59:59.999)
 * Input: "2025-11-18T00:00:00+01:00"
 * Output: "2025-11-18T23:59:59+01:00"
 */
export const toEndOfDay = (isoDate: string): string => {
	return dayjs(isoDate).endOf('day').format();
};

// ============================================================================
// FORMATTING DATES OBJECTS FOR DISPLAY
// ============================================================================

/**
 * Pretty date for labels (localized).
 * - single: "12 jun 2025" || "12 June 2025" || "12/06/2025"
 * - month:  "Junio 2025"
 * - includeYear: false drops the year (e.g. "12 jun") — defaults to true so existing callers are unaffected.
 */
export const formatDateForDisplay = (
	date: Date,
	mode: DatePickerMode = 'single',
	month: 'short' | 'long' | 'numeric' = 'short',
	showDayOfWeek: boolean = false,
	includeYear: boolean = true,
): string => {
	if (isNaN(date.getTime())) return '';
	const locale = getLanguage();
	const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
	if (mode === 'month') {
		const monthLabel = date.toLocaleDateString(locale, { month: 'long' });
		return `${capitalize(monthLabel)} ${date.getFullYear()}`;
	}
	const options: Intl.DateTimeFormatOptions = {
		timeZone: getBrowserTimezone(),
		day: '2-digit',
		month: month,
	};
	if (includeYear) {
		options.year = 'numeric';
	}
	if (showDayOfWeek) {
		options.weekday = 'short';
	}
	return date.toLocaleDateString(locale, options).replace(',', '');
};

/**
 * Date object -> localized "DD/MM/YYYY, HH:mm" for display.
 */

export const formatDateTimeForDisplay = (date?: Date) => {
	const locale = getLanguage();
	if (!date || isNaN(date.getTime())) return '';
	return date.toLocaleString(locale, {
		timeZone: getBrowserTimezone(),
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
};

/** Date object --> localized "HH:MM" for display  */
export const formatTimeForDisplay = (date?: Date) => {
	const locale = getLanguage();
	if (!date || typeof date === 'string' || isNaN(date?.getTime())) return '';
	return date.toLocaleTimeString(locale, {
		timeZone: getBrowserTimezone(),
		hour: '2-digit',
		minute: '2-digit',
	});
};

// ============================================================================
// INPUT HELPERS AND VALIDATORS
// ============================================================================

export const isFullISO = (s?: string) => !!s && ISO_RE.test(s);

/**
 * Get start and end of day range from an ISO date string
 * Input: "2025-11-18T00:00:00+01:00" (already at midnight)
 * Output: { start: "2025-11-18T00:00:00+01:00", end: "2025-11-18T23:59:59+01:00" }
 */
export const getFullDayRange = (isoDate: string): { start: string; end: string } => {
	const start = isoDate;

	// End of day: 23:59:59.999 in the same timezone
	const end = dayjs(isoDate).endOf('day').format();

	return { start, end };
};

/**
 * Get first day of month as full ISO 8601 string at midnight
 * Example: any date in November 2025 -> "2025-11-01T00:00:00+01:00"
 */
export const getFirstDayOfMonthISO = (date = new Date()): string => {
	const userTimezone = dayjs.tz.guess();

	return dayjs(date)
		.tz(userTimezone)
		.startOf('month') // First day at 00:00:00
		.format(); // Full ISO with timezone
};

/**
 * Get full month range (first day 00:00:00 to last day 23:59:59) from an ISO date string
 * Input: "2025-11-15T00:00:00+01:00" (any day in the month)
 * Output: { start: "2025-11-01T00:00:00+01:00", end: "2025-11-30T23:59:59+01:00" }
 */
export const getMonthRange = (isoDate: string): { start: string; end: string } => {
	const start = dayjs(isoDate).startOf('month').format();
	const end = dayjs(isoDate).endOf('month').format();

	return { start, end };
};

/**
 * Get full month range in UTC from a YYYY-MM string.
 * Input: "2026-04"
 * Output: { min: "2026-04-01T00:00:00.000Z", max: "2026-04-30T23:59:59.999Z" }
 */
export const getMonthRangeFromYearMonth = (selectedMonth: string): { min: string; max: string } => {
	const [year, month] = selectedMonth.split('-').map(Number);
	const start = new Date(year as number, (month as number) - 1, 1);
	const end = new Date(year as number, month as number, 0, 23, 59, 59, 999);
	return {
		min: start.toISOString(),
		max: end.toISOString(),
	};
};

/***
 * Format Date object to "DD/MM/YYYY" to display inputs
 */

export const formatDateForInput = (date: Date) => {
	if (isNaN(date.getTime())) return '';
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${d}/${m}/${y}`;
};

/***
 * * Parse "DD/MM/YYYY" formatted string to Date object
 */

export const parseInputDate = (value: string): Date | null => {
	const trimmed = value.trim();
	if (!trimmed) return null;

	const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
	if (!match) return null;

	const day = Number(match[1]);
	const month = Number(match[2]);
	const year = Number(match[3]);

	const d = new Date(year, month - 1, day);
	if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
		return null;
	}
	return d;
};

/**
 * Zero-out the time component (LOCAL midnight).
 */
export const stripTime = (d: Date) => {
	const x = new Date(d);
	x.setHours(0, 0, 0, 0);
	return x;
};

export const monthStart = (y: number, m: number) => new Date(y, m, 1, 0, 0, 0, 0);
export const monthEnd = (y: number, m: number) => new Date(y, m + 1, 0, 23, 59, 59, 999);

export const toYearMonth = (d: Date) =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export const fromYearMonth = (ym: string) => {
	const [yRaw, mRaw] = ym.split('-').map(Number);
	const y = yRaw ?? 1970;
	const m = mRaw ?? 1;
	return new Date(y, m - 1, 1, 0, 0, 0, 0);
};

/**
 * Compare if two dates fall on the same local calendar day.
 */
export const isSameDay = (a?: Date | null, b?: Date | null) =>
	!!a && !!b && stripTime(a).getTime() === stripTime(b).getTime();

/**
 * Ensure a <= b (LOCAL day granularity).
 */
export const normalizeRange = (a: Date, b: Date) =>
	stripTime(a).getTime() <= stripTime(b).getTime() ? ([a, b] as const) : ([b, a] as const);

/**
 * Inclusive day count between two local dates.
 */
export const inclusiveDays = (a: Date, b: Date) =>
	Math.floor(Math.abs(stripTime(b).getTime() - stripTime(a).getTime()) / DAYS_IN_MILLISECONDS) + 1;

/**
 * Extract only the local calendar date "YYYY-MM-DD" from an ISO datetime.
 * Useful to feed <input type="date">.
 */
export const extractLocalDateFromISO = (iso?: string): string | undefined => {
	if (!iso) return undefined;
	const date = new Date(iso);
	return toISODateLocal(date);
};

/**
 * Extract only the local "HH:mm" from an ISO datetime.
 *
 *     If the ISO is a "time-only" value anchored to a historical date
 *     like "1900-01-01T10:30:00+00:00", time zone databases can apply
 *     weird historical offsets (e.g., Barcelona ~ -00:14 in 1900) and
 *     yield 15-minute shifts.
 *
 * To avoid that, we:
 *  1) Read UTC parts from the ISO.
 *  2) Rebuild an instant on a modern/actual anchor day in UTC.
 *  3) Read local HH:mm from that safe anchor.
 */
/** Extract HH:mm from an ISO string that is already in local time (no UTC conversion). */
export const formatLocalTimeFromISO = (iso?: string | null): string | undefined => {
	if (!iso) return undefined;
	const d = new Date(iso);
	return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const extractLocalTimeFromISO = (
	iso?: string,
	anchorDate: Date = new Date(),
): string | undefined => {
	if (!iso) return undefined;

	const dt = new Date(iso); // UTC instant (handles Z/+00:00)
	const utcH = dt.getUTCHours();
	const utcM = dt.getUTCMinutes();

	const anchored = new Date(
		Date.UTC(
			anchorDate.getFullYear(),
			anchorDate.getMonth(),
			anchorDate.getDate(),
			utcH,
			utcM,
			0,
			0,
		),
	);

	const hh = String(anchored.getHours()).padStart(2, '0');
	const mm = String(anchored.getMinutes()).padStart(2, '0');
	return `${hh}:${mm}`;
};

/** "HH:mm" local → "HH:mm" UTC */
export const localTimeToUTC = (localTime: string): string => {
	const iso = convertLocalTimeToISO(localTime, new Date());
	if (!iso) return localTime;
	const dt = new Date(iso);
	const hh = String(dt.getUTCHours()).padStart(2, '0');
	const mm = String(dt.getUTCMinutes()).padStart(2, '0');
	return `${hh}:${mm}`;
};

/** Truncates a "HH:MM:SS" time string (the API's format) down to "HH:MM" for time-picker inputs. */
export const toHourMinute = (time: string | null): string | null =>
	time ? time.slice(0, 5) : time;

/** "HH:mm" UTC → "HH:mm" local */
export const utcTimeToLocal = (utcTime: string): string => {
	const today = new Date();
	const [hh, mm] = utcTime.split(':').map(Number);
	const utcISO = new Date(
		Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), hh, mm),
	).toISOString();
	return extractLocalTimeFromISO(utcISO) ?? utcTime;
};

/**+
 * Format a day number with ordinal suffix according to locale (1st, 2nd, 3rd, 4th, etc).
 * Optionally include month as string.
 */

export const formatDayToLocale = ({
	day,
	month,
}: {
	day: number | string;
	month?: number | string;
}): { day: string; month?: string } => {
	const locale = getLanguage();
	const dayNum = typeof day === 'string' ? parseInt(day, 10) : day;

	try {
		const pr = new Intl.PluralRules(locale, { type: 'ordinal' });
		const rule = pr.select(dayNum);
		const formatter = new Intl.NumberFormat(locale);
		const formattedNumber = formatter.format(dayNum);

		let formattedDay = formattedNumber;
		if (locale.startsWith('en')) {
			const suffixes: Record<string, string> = {
				one: 'st',
				two: 'nd',
				few: 'rd',
				other: 'th',
			};
			formattedDay = `${formattedNumber}${suffixes[rule]}`;
		}

		if (month !== undefined) {
			return {
				day: formattedDay,
				month: month.toString(),
			};
		}

		return { day: formattedDay };
	} catch {
		return {
			day: dayNum.toString(),
			month: month?.toString(),
		};
	}
};

// Localized short labels for weekdays (0 = Monday … 6 = Sunday, same indexing as
// getWeekDaysOptions), always with the first letter capitalized.
// e.g. en → "Mon, Tue"; es → "Lun, Mar"; pt → "Seg., Ter.".
export const getShortWeekDayLabels = (days: number[]): string => {
	const formatter = new Intl.DateTimeFormat(getLanguage(), { weekday: 'short' });
	return (
		days
			// 2024-01-01 was a Monday → base date to map index 0..6 to Mon..Sun
			.map((day) => capitalizeFirstLetter(formatter.format(new Date(2024, 0, 1 + day))))
			.join(', ')
	);
};

/**
 * Weekday index (0 = Monday … 6 = Sunday, same indexing as getWeekDaysOptions) for a
 * date-only string ("YYYY-MM-DD"). Reads in UTC: date-only strings parse as UTC midnight
 * in JS, so reading them back via the local timezone can shift the result to the
 * previous/next day for timezones behind/ahead of UTC.
 */
export const getDayOfWeekFromDate = (dateStr: string): number => (dayjs.utc(dateStr).day() + 6) % 7;

/**
 * Next date (today excluded) that falls on the given weekday (0 = Monday … 6 = Sunday,
 * same indexing as getWeekDaysOptions). If today already matches, jumps a full week ahead
 * rather than returning today.
 */
export const getNextWeekdayOccurrence = (dayOfTheWeek: number): Date => {
	const today = dayjs();
	const todayMondayFirst = (today.day() + 6) % 7;
	const daysUntilNext = (dayOfTheWeek - todayMondayFirst + 7) % 7 || 7;
	return today.add(daysUntilNext, 'day').toDate();
};
